import React, { useMemo } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  getCurrentIsoWeekContext,
  getIsoWeekRange,
  isFutureIsoWeek,
  listSelectableWeekOptions,
  listWeekOptionsIncludingSelection,
  normalizeIsoWeekPair,
  parseWeekOptionKey,
  weekOptionKey,
} from "@/utils/isoWeek";

export const TL_WEEK_PICKER_ALL = "__all_weeks__";

const TlIsoWeekPicker = ({
  isoYear,
  week,
  onChange,
  isDark,
  inputClass,
  compact = false,
  className = "",
  allLabel = "Tüm kayıtlar",
}) => {
  const nowCtx = useMemo(() => getCurrentIsoWeekContext(), []);
  const filterActive = week !== null && week !== undefined && week !== "";

  const { isoYear: safeYear, week: safeWeek } = filterActive
    ? normalizeIsoWeekPair(isoYear, week, nowCtx)
    : { isoYear: nowCtx.isoYear, week: null };

  const weekOptions = useMemo(() => {
    if (filterActive) {
      return listWeekOptionsIncludingSelection(safeYear, safeWeek);
    }
    return listSelectableWeekOptions();
  }, [filterActive, safeYear, safeWeek]);

  const selectedKey = filterActive ? weekOptionKey(safeYear, safeWeek) : TL_WEEK_PICKER_ALL;
  const selectValue = filterActive && weekOptions.some((o) => o.key === selectedKey)
    ? selectedKey
    : TL_WEEK_PICKER_ALL;

  const applyKey = (key) => {
    if (key === TL_WEEK_PICKER_ALL) {
      onChange({ cleared: true });
      return;
    }
    let { isoYear: y, week: w } = parseWeekOptionKey(key);
    ({ isoYear: y, week: w } = normalizeIsoWeekPair(y, w, nowCtx));
    if (isFutureIsoWeek(y, w)) {
      y = nowCtx.isoYear;
      w = nowCtx.week;
    }
    const range = getIsoWeekRange(y, w);
    onChange({ isoYear: y, week: w, start: range.start, end: range.end });
  };

  const triggerClass = compact
    ? `h-8 text-[11px] min-w-[140px] max-w-[200px] ${inputClass}`
    : `h-9 text-xs min-w-[180px] max-w-[240px] ${inputClass}`;

  if (weekOptions.length === 0) {
    return (
      <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>—</span>
    );
  }

  return (
    <Select value={selectValue} onValueChange={applyKey}>
      <SelectTrigger className={`${triggerClass} ${className}`}>
        <SelectValue placeholder={allLabel} />
      </SelectTrigger>
      <SelectContent className={`max-h-72 ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
        <SelectItem value={TL_WEEK_PICKER_ALL} className="text-xs font-medium">
          {allLabel}
        </SelectItem>
        {weekOptions.map((opt) => (
          <SelectItem key={opt.key} value={opt.key} className="text-xs">
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default TlIsoWeekPicker;
